/**
 * Folder filter chips for presets modal header
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo } from 'react';

import { Button } from '@/components/ui';

import type { Preset } from '../shared';

export interface PresetFolderChipsProps {
  catalogFolders: Array<{ id: string; label: string }>;
  presets: Preset[];
  activeFolderId: string | null;
  onSelect: (folderId: string | null) => void;
  onManageFolders?: () => void;
}

export const PresetFolderChips: FC<PresetFolderChipsProps> = ({
  catalogFolders,
  presets,
  activeFolderId,
  onSelect,
  onManageFolders,
}) => {
  const merged = useMemo(() => {
    const byId = new Map<string, string>();
    for (const t of catalogFolders) byId.set(t.id, t.label);
    for (const p of presets) {
      for (const t of p.folders ?? []) {
        if (!byId.has(t.id)) byId.set(t.id, t.label);
      }
    }
    return [...byId.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [catalogFolders, presets]);

  if (merged.length === 0 && onManageFolders == null) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border/40 bg-muted/10 px-4 py-2.5">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">Folders</span>
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={`rounded-full px-2.5 py-0.5 text-xs ${
            activeFolderId == null ? 'bg-primary text-primary-foreground' : 'bg-muted/60 text-muted-foreground hover:bg-muted'
          }`}
        >
          All
        </button>
        {merged.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onSelect(t.id)}
            className={`max-w-[8rem] truncate rounded-full px-2.5 py-0.5 text-xs ${
              activeFolderId === t.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/60 text-muted-foreground hover:bg-muted'
            }`}
            title={t.label}
          >
            {t.label}
          </button>
        ))}
      </div>
      {onManageFolders != null && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={onManageFolders}
        >
          Manage…
        </Button>
      )}
    </div>
  );
};
